import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, Loader2, CheckCircle, XCircle, AlertTriangle, HelpCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTIES = [
  "Australian Labor Party", "Liberal Party", "Australian Greens", "The Nationals",
  "One Nation", "Liberal National Party", "Teal Independents", "United Australia Party",
  "Jacqui Lambie Network", "Country Liberal Party",
];

const RATINGS = {
  TRUE: { icon: CheckCircle, color: "#10B981", bg: "#D1FAE5", label: "TRUE" },
  "MOSTLY TRUE": { icon: CheckCircle, color: "#2E5948", bg: "#D1FAE5", label: "MOSTLY TRUE" },
  MISLEADING: { icon: AlertTriangle, color: "#D97706", bg: "#FEF3C7", label: "MISLEADING" },
  FALSE: { icon: XCircle, color: "#C04E3F", bg: "#FEE2E2", label: "FALSE" },
  UNVERIFIED: { icon: HelpCircle, color: "#6B7280", bg: "#F3F4F6", label: "UNVERIFIED" },
};

function extractRating(text) {
  for (const key of Object.keys(RATINGS)) {
    if (text.toUpperCase().includes(key)) return key;
  }
  return "UNVERIFIED";
}

function renderMd(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
}

export default function FactCheckerPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [claim, setClaim] = useState("");
  const [party, setParty] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "#334155" : "#000000";

  const checkFact = async () => {
    if (!claim.trim() || claim.trim().length < 10) {
      setError("Please enter a political claim to check (at least 10 characters).");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/fact-check`, {
        claim: claim.trim(),
        party_name: party || null,
      });
      setResult(res.data);
    } catch {
      setError("Failed to check the claim. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const rating = result ? extractRating(result.verdict) : null;
  const ratingInfo = rating ? RATINGS[rating] : null;
  const RatingIcon = ratingInfo?.icon;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="fact-checker-page">
      {/* Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="fact-check-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Policy Fact Checker
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Fact Check a Political Claim
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Heard a political claim and not sure if it's true? Our AI will verify it against Australian political records and party policies.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Input card */}
        <div
          className="border-2 border-black p-6 mb-6"
          style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
        >
          <label className="block font-black text-sm mb-2" style={{ color: text }}>
            Enter a Political Claim
          </label>
          <textarea
            data-testid="claim-input"
            value={claim}
            onChange={(e) => setClaim(e.target.value)}
            placeholder='e.g. "Labor will raise taxes on everyone" or "The Liberals support cutting Medicare"'
            rows={4}
            className="w-full border-2 border-black px-4 py-3 text-sm font-medium focus:outline-none resize-none mb-4"
            style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text, borderColor: borderC }}
          />
          <label className="block font-black text-sm mb-2" style={{ color: text }}>
            Attributed to (optional)
          </label>
          <select
            data-testid="party-select"
            value={party}
            onChange={(e) => setParty(e.target.value)}
            className="w-full border-2 border-black px-4 py-3 text-sm font-medium focus:outline-none mb-4"
            style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text, borderColor: borderC }}
          >
            <option value="">— Select a party (optional) —</option>
            {PARTIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          {error && (
            <p className="text-sm font-medium mb-3" style={{ color: "#C04E3F" }}>
              {error}
            </p>
          )}
          <button
            data-testid="check-fact-btn"
            onClick={checkFact}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 text-white font-black border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            style={{ backgroundColor: "#2E5948", boxShadow: "4px 4px 0px 0px #000" }}
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking...</> : <><Search className="w-4 h-4" /> Check This Claim</>}
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div
            className="border-2 border-black p-10 text-center"
            style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
          >
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: "#2E5948" }} />
            <p className="font-bold" style={{ color: text }}>Analysing claim against party records...</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div
            className="border-2 border-black overflow-hidden"
            style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
            data-testid="fact-result"
          >
            {ratingInfo && (
              <div
                className="flex items-center gap-3 px-6 py-4 border-b-2 border-black"
                style={{ backgroundColor: ratingInfo.bg }}
              >
                {RatingIcon && (
                  <RatingIcon className="w-7 h-7 flex-shrink-0" style={{ color: ratingInfo.color }} />
                )}
                <div>
                  <div
                    className="text-2xl font-black"
                    style={{ fontFamily: "Outfit, sans-serif", color: ratingInfo.color }}
                  >
                    {ratingInfo.label}
                  </div>
                  <p className="text-xs font-medium" style={{ color: ratingInfo.color }}>
                    AI Verdict
                  </p>
                </div>
              </div>
            )}
            <div className="p-6">
              <div
                className="text-sm leading-relaxed"
                style={{ color: text, fontFamily: "Public Sans, sans-serif" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${renderMd(result.analysis)}</p>`) }}
              />
              <p className="text-xs mt-5" style={{ color: textSec }}>
                AI fact-check for civic education purposes only. Always verify with primary sources such as party websites and the AEC.
              </p>
            </div>
          </div>
        )}

        {/* Example claims */}
        <div className="mt-8">
          <p className="text-sm font-bold mb-3" style={{ color: textSec }}>Try an example:</p>
          <div className="flex flex-wrap gap-2">
            {[
              "Labor has cut defence spending",
              "The Greens want to ban all new housing developments",
              "The Liberals introduced the NDIS",
            ].map((ex) => (
              <button
                key={ex}
                onClick={() => setClaim(ex)}
                className="text-xs px-3 py-1.5 border-2 border-black font-medium hover:bg-[#FFC82E] transition-colors"
                style={{ color: text }}
              >
                "{ex}"
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
